/**
 * Daily mode utilities for managing daily challenge state in localStorage
 */

export interface DailyScore {
    date: string; // YYYY-MM-DD format
    score: number;
    roundScores: number[]; // Score for each round
    completedAt: string; // ISO timestamp
}

const DAILY_SCORE_KEY = 'eorguessr_daily_score';

/**
 * Gets today's UTC date string in YYYY-MM-DD format
 */
export function getTodayDateString(): string {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Gets the time until the next daily reset (midnight UTC)
 * Returns an object with hours, minutes, and seconds
 */
export function getTimeUntilNextDaily(): { hours: number; minutes: number; seconds: number } {
    const now = new Date();
    const tomorrow = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0
    ));
    
    const diff = tomorrow.getTime() - now.getTime();
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds };
}

/**
 * Formats the time until next daily as a string
 */
export function formatTimeUntilNextDaily(): string {
    const { hours, minutes, seconds } = getTimeUntilNextDaily();
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Converts UTC midnight to local time string
 */
export function getNextDailyLocalTime(): string {
    const now = new Date();
    const tomorrow = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0
    ));
    
    return tomorrow.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
}

/**
 * Gets the saved daily score from localStorage
 */
export function getDailyScore(): DailyScore | null {
    if (typeof window === 'undefined') return null;
    
    try {
        const stored = localStorage.getItem(DAILY_SCORE_KEY);
        if (!stored) return null;
        
        const score = JSON.parse(stored) as DailyScore;
        return score;
    } catch (error) {
        console.error('Error reading daily score:', error);
        return null;
    }
}

/**
 * Checks if today's daily has been completed
 */
export function isTodayDailyCompleted(): boolean {
    const score = getDailyScore();
    if (!score) return false;
    
    const today = getTodayDateString();
    return score.date === today;
}

/**
 * Gets today's daily score if completed
 */
export function getTodayDailyScore(): number | null {
    const score = getDailyScore();
    if (!score) return null;
    
    const today = getTodayDateString();
    if (score.date !== today) return null;
    
    return score.score;
}

/**
 * Gets today's full daily score data if completed
 */
export function getTodayDailyScoreData(): DailyScore | null {
    const score = getDailyScore();
    if (!score) return null;
    
    const today = getTodayDateString();
    if (score.date !== today) return null;
    
    return score;
}

/**
 * Saves the daily score to localStorage
 * Only saves if this is the first completion of today's daily
 */
export function saveDailyScore(score: number, roundScores: number[] = []): boolean {
    if (typeof window === 'undefined') return false;
    
    // Check if today's daily was already completed
    if (isTodayDailyCompleted()) {
        console.log('Daily already completed, not updating score');
        return false;
    }
    
    try {
        const dailyScore: DailyScore = {
            date: getTodayDateString(),
            score: score,
            roundScores: roundScores,
            completedAt: new Date().toISOString()
        };
        
        localStorage.setItem(DAILY_SCORE_KEY, JSON.stringify(dailyScore));
        console.log('Daily score saved:', dailyScore);
        return true;
    } catch (error) {
        console.error('Error saving daily score:', error);
        return false;
    }
}

/**
 * Clears the daily score (for testing purposes)
 */
export function clearDailyScore(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(DAILY_SCORE_KEY);
}

/**
 * Calculate the daily number based on days since game launch
 * Using a fixed epoch date: February 2, 2026
 * Uses UTC midnight boundary to match daily reset timing
 */
export function getDailyNumber(): number {
    const epochDate = new Date(Date.UTC(2026, 1, 2, 0, 0, 0, 0)); // February 2, 2026 00:00 UTC
    const today = new Date();
    const todayUTC = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate(),
        0, 0, 0, 0
    ));
    
    const diffTime = todayUTC.getTime() - epochDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays + 1; // +1 so first day is #1 not #0
}
